import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '@/components/molecules/ContentBlock';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import EmptyState from '@/components/molecules/EmptyState';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DistributionStats, WeightDistributionHistogram } from '@/domain/cages';
import { OverviewChartCard } from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { localizedDateFormat } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import WeightDistributionChartToggles from './components/WeightDistributionChartToggles';
import WeightDistributionPlayControl from './components/WeightDistributionPlayControl';
import WeightDistributionSummary from './components/WeightDistributionSummary';
import {
    buildWeightDistributionChartSpec,
    DISTRIBUTION_ANIMATION_DURATION,
    resolveBucketWeightUnit,
    totalMeasurementCount,
} from './weightDistributionChartSpec';

const CHART_HEIGHT = 400;
const INFO_COLUMN_WIDTH = 375;
const NO_ANIMATION = 0;
/** `DateRangePicker`'s own default, kept now that the card sets one explicitly. */
const DEFAULT_DATE_PRESET = 'last4weeks';

export interface CageWeightDistributionViewProps {
    /** Histogram of the day currently selected. */
    histogram: WeightDistributionHistogram[];
    stats: DistributionStats;
    /** Day currently on the chart, `YYYY-MM-DD`. */
    day: string;
    /** Index of `day` within the loaded range, for the scrubber. */
    currentIndex: number;
    dayCount: number;
    onIndexChange: (index: number) => void;
    weightUnitTag: string;
    isAggregated: boolean;
    onAggregatedChange: (isAggregated: boolean) => void;
    isSmolt: boolean;
    showNamedFish: boolean;
    fishName: string;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (dates: DateRange) => void;
}

export default function CageWeightDistributionView(props: CageWeightDistributionViewProps) {
    const {
        histogram,
        stats,
        day,
        currentIndex,
        dayCount,
        onIndexChange,
        weightUnitTag,
        isAggregated,
        onAggregatedChange,
        isSmolt,
        showNamedFish,
        fishName,
        isLoading,
        error,
        retry,
        dateFrom,
        dateTo,
        onDateChange,
    } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);
    const [isAnimated, setIsAnimated] = useState(false);

    const spec = useMemo(
        () =>
            buildWeightDistributionChartSpec({
                histogram,
                weightUnitTag,
                seriesLabel: t('cageWeight.distribution.shareInInterval'),
                axisLabel: t('cageWeight.distribution.shareInInterval'),
                formattedWeightUnit: t(`weightUnit.${resolveBucketWeightUnit(weightUnitTag)}`),
                countLabel: t('cageWeight.weightDistribution.measurements'),
                locale,
                animationDuration: isAnimated ? DISTRIBUTION_ANIMATION_DURATION : NO_ANIMATION,
            }),
        [histogram, weightUnitTag, t, locale, isAnimated]
    );

    // Null rather than 0 for an empty histogram, so the panel shows a dash
    // instead of claiming the pen was measured zero times.
    const measurementCount = useMemo(
        () => (histogram.length === 0 ? null : totalMeasurementCount(histogram)),
        [histogram]
    );

    // Distinguishes "the request finished and this cage has nothing" from the
    // loading and error states, which the chart card renders itself.
    const isEmpty = !isLoading && !error && dayCount === 0;

    return (
        <>
            <GridLayout contentLayout="1-fixed" secondColumnWidth={INFO_COLUMN_WIDTH}>
                {isEmpty ? (
                    // The picker has to stay reachable here, or a range with no
                    // measurements would be a dead end.
                    <ContentBlock
                        title={t('common.labels.livingWeight')}
                        headerActions={
                            <DateRangePicker
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                onDateChange={onDateChange}
                                defaultPresetId={DEFAULT_DATE_PRESET}
                                menuPosition="right"
                            />
                        }>
                        <EmptyState
                            title={t('cageWeight.distribution.noDistributionData')}
                            appearance="quiet"
                            size="spacious"
                        />
                    </ContentBlock>
                ) : (
                    <OverviewChartCard
                        title={t('common.labels.livingWeight')}
                        subtitle={
                            day
                                ? localizedDateFormat({
                                      dateString: day,
                                      onlydate: true,
                                      locale: i18n.language,
                                  })
                                : ' '
                        }
                        chartName="cage-weight-distribution"
                        csvExport={false}
                        spec={spec}
                        chartHeight={CHART_HEIGHT}
                        isLoading={isLoading}
                        error={error}
                        {...retry}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                        defaultPresetId={DEFAULT_DATE_PRESET}
                        legendRight={
                            <WeightDistributionChartToggles
                                isAnimated={isAnimated}
                                onAnimatedChange={setIsAnimated}
                                isAggregated={isAggregated}
                                onAggregatedChange={onAggregatedChange}
                                disabled={isLoading || histogram.length === 0}
                            />
                        }
                        footer={
                            <WeightDistributionPlayControl
                                currentIndex={currentIndex}
                                dayCount={dayCount}
                                onIndexChange={onIndexChange}
                            />
                        }
                    />
                )}
                <ContentBlock title={t('common.labels.livingWeight')}>
                    <WeightDistributionSummary
                        stats={stats}
                        day={day}
                        measurementCount={measurementCount}
                        weightUnitTag={weightUnitTag}
                        isSmolt={isSmolt}
                        showNamedFish={showNamedFish}
                        fishName={fishName}
                        isLoading={isLoading}
                    />
                </ContentBlock>
            </GridLayout>
        </>
    );
}
